import { useState, useEffect } from 'react'
import { useParams, Link, useNavigate } from 'react-router-dom'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { adminApi } from '@/lib/api'
import { cn } from '@/lib/utils'
import { ChevronLeft, CheckCircle, XCircle, Trash2, AlertTriangle, Info } from 'lucide-react'
import { COMPONENT_CATEGORIES, DATA_REQUIREMENTS, PAGE_SCOPES } from '@/types/admin'
import type { UpdateComponentPayload, FieldDef } from '@/types/admin'

function JsonField({
  label,
  value,
  onChange,
  placeholder,
  hint,
  rows = 4,
}: {
  label: string
  value: string
  onChange: (v: string) => void
  placeholder?: string
  hint?: string
  rows?: number
}) {
  const [error, setError] = useState<string | null>(null)

  function handleChange(v: string) {
    onChange(v)
    if (!v.trim()) { setError(null); return }
    try { JSON.parse(v); setError(null) } catch { setError('Format JSON tidak valid') }
  }

  return (
    <div className="space-y-1">
      <label className="text-sm font-medium">{label}</label>
      <textarea
        value={value}
        onChange={(e) => handleChange(e.target.value)}
        rows={rows}
        placeholder={placeholder}
        className="w-full rounded-md border border-input bg-background px-3 py-2 text-xs font-mono focus:outline-none focus:ring-2 focus:ring-ring resize-y"
      />
      {error && <p className="text-xs text-destructive">{error}</p>}
      {hint && !error && <p className="text-xs text-muted-foreground">{hint}</p>}
    </div>
  )
}

export default function ComponentDetailPage() {
  const { id } = useParams<{ id: string }>()
  const qc = useQueryClient()
  const navigate = useNavigate()

  const { data: component, isLoading } = useQuery({
    queryKey: ['admin', 'components', id],
    queryFn: () => adminApi.getComponent(id!),
    enabled: !!id,
  })

  const { data: plans = [] } = useQuery({
    queryKey: ['admin', 'plans'],
    queryFn: adminApi.getPlans,
  })

  // ── Edit form state ─────────────────────────────────────────────────────────
  const [form, setForm] = useState({
    label: '',
    category: '',
    description: '',
    data_requirement: '',
    page_scope: '',
    sort_order: '0',
    is_active: true,
    default_settings: '{}',
    settings_schema: '[]',
  })

  const [savedInfo, setSavedInfo] = useState(false)

  useEffect(() => {
    if (!component) return
    setForm({
      label: component.label,
      category: component.category,
      description: component.description,
      data_requirement: component.data_requirement ?? '',
      page_scope: component.page_scope ?? '',
      sort_order: String(component.sort_order),
      is_active: component.is_active,
      default_settings: JSON.stringify(component.default_settings, null, 2),
      settings_schema: JSON.stringify(component.settings_schema, null, 2),
    })
    setSelectedPlanIds(new Set(component.component_plan_access.map((a) => a.plan_id)))
  }, [component])

  const set = (field: keyof typeof form, value: string | boolean) =>
    setForm((prev) => ({ ...prev, [field]: value }))

  function isJsonValid(v: string) {
    try { JSON.parse(v || '{}'); return true } catch { return false }
  }

  const updateInfo = useMutation({
    mutationFn: () => {
      const payload: UpdateComponentPayload = {
        label: form.label,
        category: form.category,
        description: form.description,
        data_requirement: form.data_requirement || null,
        page_scope: form.page_scope || null,
        sort_order: parseInt(form.sort_order, 10) || 0,
        is_active: form.is_active,
        default_settings: JSON.parse(form.default_settings || '{}') as Record<string, unknown>,
        settings_schema: JSON.parse(form.settings_schema || '[]') as FieldDef[],
      }
      return adminApi.updateComponent(id!, payload)
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'components'] })
      qc.invalidateQueries({ queryKey: ['admin', 'components', id] })
      setSavedInfo(true)
      setTimeout(() => setSavedInfo(false), 3000)
    },
  })

  // ── Plan access state ───────────────────────────────────────────────────────
  const [selectedPlanIds, setSelectedPlanIds] = useState<Set<string>>(new Set())
  const [savedPlans, setSavedPlans] = useState(false)

  function togglePlan(planId: string) {
    setSelectedPlanIds((prev) => {
      const next = new Set(prev)
      if (next.has(planId)) next.delete(planId)
      else next.add(planId)
      return next
    })
  }

  const updatePlans = useMutation({
    mutationFn: () =>
      adminApi.updateComponentPlans(id!, Array.from(selectedPlanIds)),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['admin', 'components', id] })
      setSavedPlans(true)
      setTimeout(() => setSavedPlans(false), 3000)
    },
  })

  // ── Delete ──────────────────────────────────────────────────────────────────
  const [deleteConfirm, setDeleteConfirm] = useState(false)

  const deleteComponent = useMutation({
    mutationFn: () => adminApi.deleteComponent(id!),
    onSuccess: () => navigate('/components'),
  })

  if (isLoading) {
    return <p className="text-sm text-muted-foreground">Memuat...</p>
  }

  if (!component) {
    return (
      <div className="space-y-4">
        <Link to="/components" className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
          <ChevronLeft className="h-4 w-4" /> Komponen
        </Link>
        <p className="text-sm text-destructive">Komponen tidak ditemukan.</p>
      </div>
    )
  }

  const infoFormValid =
    !!form.label &&
    !!form.category &&
    isJsonValid(form.default_settings) &&
    isJsonValid(form.settings_schema)

  return (
    <div className="max-w-2xl space-y-8">
      {/* Back */}
      <Link
        to="/components"
        className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
      >
        <ChevronLeft className="h-4 w-4" />
        Komponen
      </Link>

      {/* Header */}
      <div className="flex items-start gap-3">
        <div className="flex-1">
          <h1 className="text-2xl font-bold">{component.label}</h1>
          <div className="mt-1 flex items-center gap-2 flex-wrap">
            <code className="rounded bg-muted px-2 py-0.5 text-xs font-mono text-muted-foreground">
              {component.component_key}
            </code>
            <span
              className={cn(
                'inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-xs font-medium',
                component.is_active ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
              )}
            >
              {component.is_active
                ? <><CheckCircle className="h-3 w-3" />Aktif</>
                : <><XCircle className="h-3 w-3" />Nonaktif</>
              }
            </span>
          </div>
        </div>
      </div>

      {/* Section 1: Component Info */}
      <div className="rounded-xl border border-border p-5 space-y-5">
        <h2 className="font-semibold">Informasi Komponen</h2>

        <div className="space-y-1">
          <label className="text-xs font-medium text-muted-foreground uppercase tracking-wide">
            Component Key (tidak bisa diubah)
          </label>
          <code className="block rounded bg-muted px-3 py-2 text-sm font-mono text-muted-foreground">
            {component.component_key}
          </code>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            Label <span className="text-destructive">*</span>
          </label>
          <input
            type="text"
            value={form.label}
            onChange={(e) => set('label', e.target.value)}
            required
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          />
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">
            Kategori <span className="text-destructive">*</span>
          </label>
          <select
            value={form.category}
            onChange={(e) => set('category', e.target.value)}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
          >
            {COMPONENT_CATEGORIES.map((cat) => (
              <option key={cat} value={cat}>{cat}</option>
            ))}
          </select>
        </div>

        <div className="space-y-1">
          <label className="text-sm font-medium">Deskripsi</label>
          <textarea
            value={form.description}
            onChange={(e) => set('description', e.target.value)}
            rows={2}
            className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring resize-y"
          />
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div className="space-y-1">
            <label className="text-sm font-medium">Data Requirement</label>
            <select
              value={form.data_requirement}
              onChange={(e) => set('data_requirement', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Tidak ada</option>
              {DATA_REQUIREMENTS.map((d) => (
                <option key={d.value} value={d.value}>{d.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Page Scope</label>
            <select
              value={form.page_scope}
              onChange={(e) => set('page_scope', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            >
              <option value="">Semua Halaman</option>
              {PAGE_SCOPES.map((s) => (
                <option key={s.value} value={s.value}>{s.label}</option>
              ))}
            </select>
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium">Urutan</label>
            <input
              type="number"
              value={form.sort_order}
              onChange={(e) => set('sort_order', e.target.value)}
              className="w-full rounded-md border border-input bg-background px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-ring"
            />
          </div>
        </div>

        <div className="flex items-center gap-2">
          <input
            type="checkbox"
            id="is_active"
            checked={form.is_active}
            onChange={(e) => set('is_active', e.target.checked)}
            className="h-4 w-4 rounded border-input accent-primary"
          />
          <label htmlFor="is_active" className="text-sm font-medium">Aktif</label>
        </div>

        <JsonField
          label="Default Settings"
          value={form.default_settings}
          onChange={(v) => set('default_settings', v)}
          hint="JSON object. Nilai default saat tenant menambahkan komponen ini."
          rows={4}
        />

        <JsonField
          label="Settings Schema"
          value={form.settings_schema}
          onChange={(v) => set('settings_schema', v)}
          hint="JSON array of FieldDef. Menentukan field yang muncul di settings panel tenant."
          rows={6}
        />

        {updateInfo.error && (
          <p className="text-sm text-destructive">
            {(updateInfo.error as Error).message ?? 'Terjadi kesalahan.'}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={() => updateInfo.mutate()}
            disabled={updateInfo.isPending || !infoFormValid}
            className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updateInfo.isPending ? 'Menyimpan...' : 'Simpan Informasi'}
          </button>
          {savedInfo && <p className="text-sm text-green-600">Tersimpan!</p>}
        </div>
      </div>

      {/* Section 2: Plan Access */}
      <div className="rounded-xl border border-border p-5 space-y-4">
        <div>
          <h2 className="font-semibold">Akses Per Plan</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Pilih plan yang dapat menggunakan komponen ini.
          </p>
        </div>

        {/* Info callout */}
        <div className="flex gap-2 rounded-lg bg-blue-50 border border-blue-200 p-3">
          <Info className="h-4 w-4 text-blue-600 mt-0.5 shrink-0" />
          <p className="text-xs text-blue-700">
            <strong>Tidak ada plan dipilih</strong> = komponen tersedia untuk{' '}
            <strong>semua plan</strong> yang memiliki fitur Homepage Builder.
            Centang satu atau lebih plan untuk membatasi akses.
          </p>
        </div>

        {/* Plan checkboxes */}
        <div className="space-y-2">
          {plans.map((plan) => (
            <label
              key={plan.id}
              className="flex items-center gap-3 rounded-lg border border-border p-3 cursor-pointer hover:bg-muted/30 transition-colors"
            >
              <input
                type="checkbox"
                checked={selectedPlanIds.has(plan.id)}
                onChange={() => togglePlan(plan.id)}
                className="h-4 w-4 rounded border-input accent-primary"
              />
              <div className="flex-1">
                <span className="text-sm font-medium">{plan.name}</span>
                {plan.price_idr !== null && (
                  <span className="ml-2 text-xs text-muted-foreground">
                    {plan.price_idr === 0
                      ? 'Gratis'
                      : new Intl.NumberFormat('id-ID', { style: 'currency', currency: 'IDR', minimumFractionDigits: 0 }).format(plan.price_idr) + '/bulan'}
                  </span>
                )}
              </div>
              <code className="text-xs text-muted-foreground">{plan.slug}</code>
            </label>
          ))}
        </div>

        {updatePlans.error && (
          <p className="text-sm text-destructive">
            {(updatePlans.error as Error).message ?? 'Terjadi kesalahan.'}
          </p>
        )}

        <div className="flex items-center gap-3">
          <button
            onClick={() => updatePlans.mutate()}
            disabled={updatePlans.isPending}
            className="rounded-md bg-primary px-5 py-2 text-sm font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            {updatePlans.isPending ? 'Menyimpan...' : 'Simpan Akses Plan'}
          </button>
          {savedPlans && <p className="text-sm text-green-600">Tersimpan!</p>}
        </div>
      </div>

      {/* Section 3: Danger Zone */}
      <div className="rounded-xl border border-destructive/40 p-5 space-y-4">
        <h2 className="font-semibold text-destructive">Zona Bahaya</h2>

        <div className="flex gap-2 rounded-lg bg-amber-50 border border-amber-200 p-3">
          <AlertTriangle className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
          <p className="text-xs text-amber-700">
            Menghapus komponen ini tidak akan menghapusnya dari{' '}
            <code className="font-mono">homepage_sections</code> tenant yang sudah memakainya.
            Komponen tersebut hanya tidak akan ditampilkan (unknown key).
          </p>
        </div>

        <div className="flex items-center gap-3">
          <button
            onClick={() => {
              if (deleteConfirm) {
                deleteComponent.mutate()
              } else {
                setDeleteConfirm(true)
                setTimeout(() => setDeleteConfirm(false), 5000)
              }
            }}
            disabled={deleteComponent.isPending}
            className={cn(
              'inline-flex items-center gap-2 rounded-md border px-4 py-2 text-sm font-medium transition-colors disabled:opacity-50',
              deleteConfirm
                ? 'border-destructive bg-destructive text-destructive-foreground hover:bg-destructive/90'
                : 'border-destructive/50 text-destructive hover:border-destructive hover:bg-destructive/10'
            )}
          >
            <Trash2 className="h-4 w-4" />
            {deleteConfirm ? 'Klik lagi untuk konfirmasi hapus' : 'Hapus Komponen'}
          </button>
          {deleteConfirm && (
            <button
              onClick={() => setDeleteConfirm(false)}
              className="text-sm text-muted-foreground hover:text-foreground"
            >
              Batal
            </button>
          )}
        </div>
      </div>
    </div>
  )
}
